// Modelo dos templates de fluxo do marketplace.
//
// Diferente da versão antiga, o template agora carrega configuração REAL do
// fluxo (SLA por etapa, layout do mini card, transições permitidas, etiquetas
// e automações). O aplicador em App.tsx > handleCreateFromTemplate materializa
// tudo isso ao criar o fluxo — os IDs abaixo são locais ao template e são
// remapeados para IDs novos na criação.

export type TemplateCampoTipo =
  | 'texto' | 'texto_longo' | 'numero' | 'data' | 'lista' | 'checkbox'
  | 'checkbox_single' | 'checkbox_multi' | 'progresso' | 'valor' | 'anexo'
  | 'data_vencimento' | 'telefone' | 'texto_informativo';

export interface TemplateCampo {
  id: string;
  nome: string;
  tipo: TemplateCampoTipo;
  obrigatorio: boolean;
  opcoes?: string[];
  /** Cor por opção (hex) — vira pill colorida no mini card. */
  coresOpcoes?: Record<string, string>;
  /** Quando true, o valor deste campo vira o título do cartão. */
  usarComoTitulo?: boolean;
  valorMinimo?: number;
  valorMaximo?: number;
  precisao?: number;
  /** Só para tipo 'texto_informativo'. */
  textoInformativo?: string;
  ocultarTitulo?: boolean;
}

export interface TemplateEtapa {
  id: string;
  nome: string;
  campos: TemplateCampo[];
  /** SLA em horas úteis esperado para o card ficar nesta etapa. Alimenta o Dashboard. */
  tempoMaximoHoras?: number;
  /** Se true, fim de semana não conta no SLA (default recomendado para etapas úteis). */
  slaIgnorarFimDeSemana?: boolean;
  /** IDs dos campos exibidos no preview do card na coluna. Vazio = todos. */
  camposVisiveisNoCard?: string[];
  /** IDs de etapas para as quais o card pode ser movido a partir desta. Vazio = todas. */
  permiteMoverPara?: string[];
  /** Se false, não é possível criar cards direto nesta etapa (entram só por movimentação). */
  permiteCriarCards?: boolean;
  ordenacaoCards?: 'mais_recente' | 'mais_antigo' | 'vencimento_proximo' | 'vencimento_distante' | 'titulo_az' | 'titulo_za';
  /** Campos limpos quando o card RETORNA para esta etapa (ex.: reprovações). */
  limparCamposAoRetornar?: string[];
  /** Liga o escalonamento por e-mail (2x, 3x... o SLA sobe na hierarquia). Só vale com tempoMaximoHoras. */
  escalonamentoSla?: boolean;
}

export interface TemplateCard {
  id: string;
  titulo: string;
  etapaId: string;
  valores: Record<string, any>;
  /** Nomes de etiquetas do fluxo aplicadas a este card de exemplo. */
  etiquetas?: string[];
}

export interface TemplateEtiqueta {
  nome: string;
  cor: string;
}

/** Condição usada nas automações do template (campos referenciados por id local). */
export interface TemplateCondicao {
  campoId: string;
  operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio';
  valor?: string | number;
}

/**
 * Automação do template. Cobre os dois casos que funcionam sem depender de
 * cadastro da empresa (colaborador, template de e-mail, número de WhatsApp):
 * - mover: move o card conforme condição
 * - exibir_ocultar: mostra/esconde campos conforme condição
 */
export type TemplateAutomacao =
  | {
      tipo: 'mover';
      nome: string;
      etapaOrigemId: string;
      condicao: TemplateCondicao;
      etapaDestinoId: string;
      /** Destino alternativo quando a condição NÃO bate (ex.: reprovado volta). */
      etapaDestinoSenaoId?: string;
    }
  | {
      tipo: 'exibir_ocultar';
      nome: string;
      etapaOrigemId: string;
      condicao: TemplateCondicao;
      /** Campos revelados quando a condição bate (escondidos caso contrário). */
      camposAfetadosIds: string[];
    };

export interface TemplateEtapaDica {
  quemFaz: string;
  objetivo: string;
  automacoes?: string[];
  conexoes?: string[];
  ondeConfigurar?: string;
}

export interface DefaultTemplate {
  id: string;
  name: string;
  descricao: string;
  categoria: string;
  icone: string; // emoji
  etapas: TemplateEtapa[];
  cards: TemplateCard[];
  etiquetas?: TemplateEtiqueta[];
  automacoes?: TemplateAutomacao[];
  /**
   * Prazo do processo: um relógio só para o card inteiro (Flow.prazoProcesso).
   * etapasPausaIds usa ids locais do template; vazio = etapas de reprovação com reenvio.
   */
  prazoProcesso?: { horas: number; ignorarFimDeSemana?: boolean; etapasPausaIds?: string[] };
  tour: string[];
  etapasDicas?: TemplateEtapaDica[];
}

export const TEMPLATE_CATEGORIES = [
  { id: 'todos', nome: 'Todos os Templates', icone: '⭐' },
  { id: 'financeiro', nome: 'Financeiro', icone: '💰' },
  { id: 'compras', nome: 'Compras & Suprimentos', icone: '🛒' },
  { id: 'rh', nome: 'RH / Departamento Pessoal', icone: '👥' },
  { id: 'ti', nome: 'TI / Tecnologia', icone: '💻' },
  { id: 'comercial', nome: 'Comercial / Vendas', icone: '📈' },
  { id: 'varejo', nome: 'Varejo / Supermercado', icone: '🛒' },
  { id: 'marketing', nome: 'Marketing', icone: '📣' },
  { id: 'juridico', nome: 'Jurídico', icone: '⚖️' },
  { id: 'operacional', nome: 'Operacional / Projetos', icone: '⚙️' },
  { id: 'obras', nome: 'Obras / Engenharia', icone: '🏗️' },
  { id: 'imobiliario', nome: 'Incorporação / Imobiliário', icone: '🏢' },
  { id: 'publico', nome: 'Serviço Público / Governo', icone: '🏛️' },
  { id: 'arrecadacao', nome: 'Arrecadação e Tributos', icone: '💰' },
  { id: 'logistica', nome: 'Logística / Estoque', icone: '📦' },
  { id: 'manutencao', nome: 'Manutenção / Facilities', icone: '🔧' },
  { id: 'qualidade', nome: 'Qualidade / SGQ', icone: '✅' },
  { id: 'atendimento', nome: 'Atendimento / Suporte', icone: '🎧' },
  { id: 'viagens', nome: 'Viagens / Despesas', icone: '✈️' },
];

// Paleta reaproveitada nas etiquetas e nas cores de opção
export const COR = {
  verde: '#10B981',
  vermelho: '#EF4444',
  amarelo: '#F59E0B',
  azul: '#3B82F6',
  roxo: '#8B5CF6',
  cinza: '#6B7280',
  rosa: '#EC4899',
  ciano: '#14B8A6',
} as const;

/** Cores padrão para campos de decisão Sim/Não. */
export const CORES_SIM_NAO: Record<string, string> = {
  'Sim': COR.verde,
  'Não': COR.vermelho,
};

/** Cores padrão para campos de prioridade. */
export const CORES_PRIORIDADE: Record<string, string> = {
  'Baixa': COR.cinza,
  'Média': COR.azul,
  'Alta': COR.amarelo,
  'Urgente': COR.vermelho,
};
